import React, { FC } from 'react';
import { IProfileInfoImt } from 'common/components/PersonProfile/interfaces';
import { useParams } from 'react-router-dom';
import useRootSelector from 'common/hooks/useRootSelector';
import { selectPersonFallthroughCapture } from 'common/components/PersonProfile/state/selectors';
import { Grid } from '@material-ui/core';
import {
  CheckInInfo,
  DocumentsSection,
  TicketsSection,
  FallthroughBlock,
  MembershipBlock,
  ServicesSection,
  BillingActionsBlock,
} from 'common/components/PersonProfile/components';
import { PeakModules } from 'common/constants/peakModules';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import ReferralSection from 'common/components/PersonProfile/components/Referrals/ReferralSection';
import usePermissionContext from 'common/hooks/context/usePermissionContext';
import BalanceContainer from 'modules/members/containers/BalanceContainer/BalanceContainer';

interface IServicesSectionBlock {
  profile: IProfileInfoImt;
  isBiggerThanXs: boolean;
  isLessThanTablet: boolean;
  classes: Partial<ClassNameMap<string>>;
  openPOSPanel: () => void;
  isOpenPOSPanel: boolean;
  handlePOSPanelChange: (isOpen: boolean) => void;
  handleNewMemberPanelChange: (isOpen: boolean) => void;
  handleChangePackagePlanChange: (isOpen: boolean) => void;
  disablePosBtn: boolean;
}

const ServicesSectionBlock: FC<IServicesSectionBlock> = ({
  profile,
  isBiggerThanXs,
  isLessThanTablet,
  classes,
  openPOSPanel,
  isOpenPOSPanel,
  handlePOSPanelChange,
  handleNewMemberPanelChange,
  handleChangePackagePlanChange,
  disablePosBtn,
}) => {
  const { id } = useParams();
  const memberId = Number(id);
  const personId = profile.get('id');
  const fallthroughCapture = useRootSelector(selectPersonFallthroughCapture(memberId));
  const fallthroughCaptureExists = Boolean(fallthroughCapture);
  const membershipPackageExists = Boolean(profile.get('membership'));
  const { MEMBER_VIEW_DOCUMENTS } = usePermissionContext();

  return personId ? (
    <Grid container spacing={2}>
      <Grid item xs={isBiggerThanXs && isLessThanTablet ? 6 : 12}>
        <CheckInInfo homeClub={profile.get('homeClub')} barcode={profile.get('barcode') || ''} />
        <BalanceContainer
          balance={profile.get('balance') || ''}
          reward={Number(profile.get('rewardPointNumber'))}
          pastDuse={profile.get('pastDuesTotal') || 0}
          totalBalance={profile.get('totalBalance') || 0}
          personId={personId}
        />
      </Grid>
      <Grid
        item
        xs={12}
        className={isBiggerThanXs && isLessThanTablet ? classes?.buttonsWrapper : ''}
      >
        <BillingActionsBlock
          openPOSPanel={openPOSPanel}
          isOpenPOSPanel={isOpenPOSPanel}
          handlePOSPanelChange={handlePOSPanelChange}
          handleNewMemberPanelChange={handleNewMemberPanelChange}
          handleChangePackagePlanChange={handleChangePackagePlanChange}
          disablePosBtn={disablePosBtn}
          profile={profile}
          module={PeakModules.Members}
          isSmallScreen={isBiggerThanXs && isLessThanTablet}
        />
      </Grid>

      {!fallthroughCaptureExists && (
        <Grid item xs={12}>
          <MembershipBlock profile={profile} module={PeakModules.Members} />
        </Grid>
      )}

      {!membershipPackageExists && (
        <Grid item xs={12}>
          <FallthroughBlock
            module={PeakModules.Members}
            profileId={Number(profile.get('id'))}
            fallthroughCapture={fallthroughCapture}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <ServicesSection
          readOnly
          module={PeakModules.Members}
          profileId={Number(profile.get('id'))}
          profileFirstName={profile.get('firstName') || ''}
          profileLastName={profile.get('lastName') || ''}
        />
      </Grid>

      {MEMBER_VIEW_DOCUMENTS ? (
        <Grid item xs={12}>
          <DocumentsSection profileId={Number(profile.get('id'))} module={PeakModules.Members} />
        </Grid>
      ) : null}

      <Grid item xs={12}>
        <TicketsSection profile={profile} module={PeakModules.Members} />
      </Grid>
      <Grid item xs={12}>
        <ReferralSection profile={profile} module={PeakModules.Members} />
      </Grid>
    </Grid>
  ) : null;
};

export default ServicesSectionBlock;
